import React, { useState, useEffect, useMemo } from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map } from 'immutable'
import compose from 'recompose/compose'
import { connect as connectRedux } from 'react-redux'
import { connect as connectStaticText } from '../../StaticText/connect'
import Button, { TYPES } from '../../Button/index'
import Sherpa, { TYPE_SMALL_BLUE } from '../../Sherpa/index'
import { getPrimary as getPrimaryLanguage } from '../../../services/languages/index'
import { useGetPlanDataBySku } from '../../../services/plans/hooks'
import { formatPrice } from '../../../services/plans/index'
import { EUR } from '../../../services/currency/index'
import {
  getDateTime,
  getDateLocale,
  ordinalDateFormatingi18n,
  formatWithLocale,
} from '../../../services/date-time/index'
import { URL_ACCOUNT_CANCEL_CONFIRM } from '../../../services/url/constants'
import {
  CANCEL_FLOW_THREE_MONTH_OFFER_SHOWN_GA4_EVENT,
  CANCEL_FLOW_THREE_MONTH_OFFER_SELECTED_GA4_EVENT,
  CANCEL_FLOW_THREE_MONTH_OFFER_REJECTED_GA4_EVENT,
} from '../../../services/event-tracking/index'
import { formatTracking } from '../../../services/checkout/index'
import { TYPE_MY_ACCOUNT_CHANGE_PLAN_CONFIRM } from '../../../services/dialog/index'
import { getThreeMonthOfferSkus } from '../../../services/user-account/index'
import { getBoundActions } from '../../../actions/index'

function AccountCancelThreeMonthOffer (props) {
  const {
    staticText,
    auth,
    user,
    userAccount,
    inboundTracking,
    setPlanChangeSelected,
    setUserAccountCancel,
    setEventDefault,
    updateUserAccountPlan,
    renderModal,
  } = props

  const [hasSubmitted, setHasSubmitted] = useState(false)

  // Get processing state from Redux
  const isProcessing = userAccount.get('billingSubscriptionsProcessing', false)

  // Get plan change result from Redux
  const planChangeData = userAccount.getIn(['data', 'billing', 'planChange'])
  const showChangeError = planChangeData?.get('_dataError')
    || planChangeData?.get('success') === false
  const showPlanChanged = planChangeData && !showChangeError

  // Get user language and auth token
  const userLanguage = getPrimaryLanguage(user.getIn(['data', 'language']))
  const authJwt = auth.get('jwt')
  const dateFormatString = ordinalDateFormatingi18n(userLanguage)
  const dateLocale = getDateLocale(userLanguage)

  // Determine which three month offer SKUs to show based on user's current plan
  const { offerSku, comparisonSku } = getThreeMonthOfferSkus(userAccount)

  // Memoize planSkus to prevent infinite loop in useGetPlanDataBySku
  const planSkus = useMemo(
    () => [offerSku, comparisonSku],
    [offerSku, comparisonSku],
  )

  // Fetch plan data using GraphQL (three month offer and comparison plans)
  const gqlPlanData = useGetPlanDataBySku({
    planSkus,
    userLanguage,
    authJwt,
  }) || Map()

  const planComplete = gqlPlanData.get('complete', false)
  const formattedPlanData = gqlPlanData.get('plans', List()) || List()

  // Find plans by SKU
  const threeMonthPlan = formattedPlanData.find(
    (plan) => plan.get('sku') === offerSku,
  ) || Map()

  const comparisonPlan = formattedPlanData.find(
    (plan) => plan.get('sku') === comparisonSku,
  ) || Map()

  // Track GA4 event when offer is shown
  useEffect(() => {
    setEventDefault(CANCEL_FLOW_THREE_MONTH_OFFER_SHOWN_GA4_EVENT)
  }, [])

  // Open modal when plan change completes (success or error)
  useEffect(() => {
    if (hasSubmitted && !isProcessing && (showPlanChanged || showChangeError)) {
      renderModal(TYPE_MY_ACCOUNT_CHANGE_PLAN_CONFIRM, {
        hideDismiss: true,
      })
    }
  }, [hasSubmitted, isProcessing, showPlanChanged, showChangeError])

  // Get paid through date from user account
  const paidThroughDate = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paidThroughDate'])
  const formattedPaidThroughDate = getDateTime(paidThroughDate)
  const formattedPaidThroughDateLocale = formatWithLocale(
    formattedPaidThroughDate,
    dateFormatString,
    { locale: dateLocale },
  )

  // Get prices from the plans
  const currencyIso = threeMonthPlan.get('currencyIso', 'USD')
  const showCurrencyIso = currencyIso !== EUR
  const planPrice = threeMonthPlan.get('costs', List()).first() || 0
  const regularPlanPrice = parseFloat(planPrice) * 2
  const originalPrice = formatPrice(
    currencyIso,
    regularPlanPrice,
    showCurrencyIso,
    null,
    userLanguage,
  )
  const discountedPrice = formatPrice(
    currencyIso,
    planPrice,
    showCurrencyIso,
    null,
    userLanguage,
  )

  const handleAcceptOffer = () => {
    setEventDefault(CANCEL_FLOW_THREE_MONTH_OFFER_SELECTED_GA4_EVENT)
    setPlanChangeSelected(threeMonthPlan)
    setHasSubmitted(true)
    const tracking = formatTracking(inboundTracking)
    updateUserAccountPlan({
      nextRatePlanId: threeMonthPlan.get('id'),
      planSku: threeMonthPlan.get('sku'),
      auth,
      tracking,
    })
  }

  const handleCancelAnyway = () => {
    setEventDefault(CANCEL_FLOW_THREE_MONTH_OFFER_REJECTED_GA4_EVENT)
    setUserAccountCancel()
  }

  // Show loading state while plan data is being fetched
  if (threeMonthPlan.size === 0 && !planComplete) {
    return (
      <div className="account-cancel-three-month-offer__loading">
        <Sherpa type={TYPE_SMALL_BLUE} />
      </div>
    )
  }

  return (
    <div className="account-cancel-three-month-offer">
      <div className="account-cancel-three-month-offer__background"></div>
      <div className="account-cancel-three-month-offer__overlay"></div>
        <div className="account-cancel-three-month-offer__content">
          <div className="account-cancel-three-month-offer__best-value">
            {staticText.getIn(['data', 'specialOffer'])}
          </div>
          <div className="account-cancel-three-month-offer__title">
            {staticText.getIn(['data', 'titleMain'])} <span className="account-cancel-three-month-offer__title-accent">{staticText.getIn(['data', 'titleAccent'])}</span>
          </div>
          <ul className="account-cancel-three-month-offer__prop-list">
            <li className="account-cancel-three-month-offer__prop-item">
              {staticText.getIn(['data', 'payOnlyItem'])} <span className="account-cancel-three-month-offer__striked">{originalPrice}</span> <span className="account-cancel-three-month-offer__highlighted">{discountedPrice} {staticText.getIn(['data', 'fiftyOffBadge'])}</span> {staticText.getIn(['data', 'preTaxOn'])} {formattedPaidThroughDateLocale}. {staticText.getIn(['data', 'noPayNow'])}
            </li>
            <li className="account-cancel-three-month-offer__prop-item">
              {staticText.getIn(['data', 'keepAccess'])} <span className="account-cancel-three-month-offer__highlighted">{staticText.getIn(['data', 'threeMoreMonths'])}.</span>
            </li>
            <li className="account-cancel-three-month-offer__prop-item">
              {staticText.getIn(['data', 'switchAutomatically'])} {comparisonPlan.get('name', '')} {staticText.getIn(['data', 'afterwardsIfNotCancelled'])}
            </li>
            <li className="account-cancel-three-month-offer__prop-item">
              {staticText.getIn(['data', 'cancelAnytime'])}
            </li>
            <li className="account-cancel-three-month-offer__prop-item">
              <span className="account-cancel-three-month-offer__highlighted">{staticText.getIn(['data', 'bonus'])}</span> {staticText.getIn(['data', 'bonusDescription'])}
            </li>
          </ul>
          <div className="account-cancel-three-month-offer__buttons">
            <Button
              type={TYPES.PRIMARY}
              className="account-cancel-three-month-offer__button account-cancel-three-month-offer__button-accept"
              onClick={handleAcceptOffer}
              disabled={isProcessing}
            >
              {isProcessing
                ? staticText.getIn(['data', 'processing'])
                : staticText.getIn(['data', 'acceptOffer'])}
            </Button>
            <Button
              type={TYPES.SECONDARY}
              className="account-cancel-three-month-offer__button account-cancel-three-month-offer__button-cancel"
              url={URL_ACCOUNT_CANCEL_CONFIRM}
              onClick={handleCancelAnyway}
              disabled={isProcessing}
            >
              {staticText.getIn(['data', 'cancelAnyway'])}
            </Button>
          </div>
        </div>
    </div>
  )
}

AccountCancelThreeMonthOffer.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
}

export default compose(
  connectStaticText({ storeKey: 'accountCancelThreeMonthOffer' }),
  connectRedux(
    (state) => ({
      auth: state.auth,
      user: state.user,
      userAccount: state.userAccount,
      subscription: state.subscription,
      inboundTracking: state.inboundTracking,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPlanChangeSelected: actions.plans.setPlanChangeSelected,
        setUserAccountCancel: actions.userAccount.setUserAccountCancel,
        setEventDefault: actions.eventTracking.setEventDefault,
        updateUserAccountPlan: actions.userAccount.updateUserAccountPlan,
        renderModal: actions.dialog.renderModal,
      }
    },
  ),
)(AccountCancelThreeMonthOffer)
